import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { gamificationAPI, slotsAPI } from '../../services/api';
import { useToast } from '../../context/ToastContext';
import type { LeaderboardEntry } from '../../types';

const PURPLE = '#200854';

const GamificationTab: React.FC = () => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [resetConfirm, setResetConfirm] = useState(false);

  // Competition results state
  const [compSlotId, setCompSlotId] = useState('');
  const [compResults, setCompResults] = useState<{ userId: string; place: number }[]>([
    { userId: '', place: 1 },
    { userId: '', place: 2 },
    { userId: '', place: 3 },
  ]);

  const { data: leaderboard = [], isLoading: leaderboardLoading } = useQuery<LeaderboardEntry[]>({
    queryKey: ['admin-leaderboard'],
    queryFn: () => gamificationAPI.getLeaderboard(50),
  });

  // Get competition slots (isCompetition = true)
  const { data: allSlots = [] } = useQuery<any[]>({
    queryKey: ['all-slots-for-comp'],
    queryFn: () => slotsAPI.getSlots(undefined, true),
  });
  const competitionSlots = allSlots.filter((s: any) => s.isCompetition);

  // Get attendees for selected competition slot
  const selectedSlot = allSlots.find((s: any) => s.id === compSlotId);
  const attendees = selectedSlot?.attendees || [];

  const seasonResetMutation = useMutation({
    mutationFn: gamificationAPI.seasonReset,
    onSuccess: () => {
      toast.success('Sezóna resetovaná!');
      queryClient.invalidateQueries({ queryKey: ['admin-leaderboard'] });
      setResetConfirm(false);
    },
    onError: () => toast.error('Chyba pri resetovaní sezóny'),
  });

  const compResultsMutation = useMutation({
    mutationFn: () => {
      const validResults = compResults.filter(r => r.userId);
      return gamificationAPI.setCompetitionResults(compSlotId, validResults);
    },
    onSuccess: () => {
      toast.success('Výsledky súťaže uložené!');
      queryClient.invalidateQueries({ queryKey: ['admin-leaderboard'] });
      setCompSlotId('');
    },
    onError: () => toast.error('Chyba pri ukladaní výsledkov'),
  });

  return (
    <div className="space-y-8">
      {/* Leaderboard */}
      <div>
        <h3 className="text-lg font-bold mb-4" style={{ color: PURPLE }}>Leaderboard</h3>
        {leaderboardLoading ? (
          <div className="text-gray-400 text-sm">Načítavam...</div>
        ) : leaderboard.length === 0 ? (
          <div className="text-gray-400 text-sm">Žiadne body ešte neboli udelené.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left">
                  <th className="py-2 px-2">#</th>
                  <th className="py-2 px-2">Meno</th>
                  <th className="py-2 px-2">Level</th>
                  <th className="py-2 px-2 text-right">Body</th>
                  <th className="py-2 px-2 text-right">Jazdy</th>
                  <th className="py-2 px-2 text-right">Streak</th>
                </tr>
              </thead>
              <tbody>
                {leaderboard.map((entry) => (
                  <tr key={entry.userId} className="border-b hover:bg-gray-50">
                    <td className="py-2 px-2 font-bold">
                      {entry.rank <= 3 ? ['🥇', '🥈', '🥉'][entry.rank - 1] : entry.rank}
                    </td>
                    <td className="py-2 px-2">{entry.name} {entry.surname}</td>
                    <td className="py-2 px-2">
                      <span className="inline-block px-2 py-0.5 rounded-full text-xs font-bold text-white" style={{ backgroundColor: PURPLE }}>
                        {entry.level.name}
                      </span>
                    </td>
                    <td className="py-2 px-2 text-right font-bold" style={{ color: PURPLE }}>{entry.totalPoints}</td>
                    <td className="py-2 px-2 text-right">{entry.rideCount}</td>
                    <td className="py-2 px-2 text-right">{entry.currentStreak}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Competition Results */}
      <div>
        <h3 className="text-lg font-bold mb-4" style={{ color: PURPLE }}>Výsledky súťaže</h3>
        {competitionSlots.length === 0 ? (
          <div className="text-gray-400 text-sm">Žiadne súťažné eventy. Označ event ako "Súťaž" v editácii eventu.</div>
        ) : (
          <div className="space-y-4">
            <select
              value={compSlotId}
              onChange={(e) => setCompSlotId(e.target.value)}
              className="w-full border rounded px-3 py-2"
            >
              <option value="">-- Vyber súťažný event --</option>
              {competitionSlots.map((s: any) => (
                <option key={s.id} value={s.id}>
                  {s.title} ({new Date(s.date).toLocaleDateString('sk')})
                </option>
              ))}
            </select>

            {compSlotId && (
              <div className="space-y-3">
                {[1, 2, 3].map((place) => (
                  <div key={place} className="flex items-center gap-3">
                    <span className="w-8 text-center font-bold text-lg">
                      {place === 1 ? '🥇' : place === 2 ? '🥈' : '🥉'}
                    </span>
                    <select
                      value={compResults[place - 1]?.userId || ''}
                      onChange={(e) => {
                        const newResults = [...compResults];
                        newResults[place - 1] = { userId: e.target.value, place };
                        setCompResults(newResults);
                      }}
                      className="flex-1 border rounded px-3 py-2"
                    >
                      <option value="">-- Vyber jazdca --</option>
                      {attendees.map((a: any) => (
                        <option key={a.id} value={a.id}>
                          {a.name || a.email} {a.surname || ''}
                        </option>
                      ))}
                    </select>
                    <span className="text-sm text-gray-500 w-16 text-right">
                      +{place === 1 ? 30 : place === 2 ? 20 : 10} b.
                    </span>
                  </div>
                ))}
                <button
                  onClick={() => compResultsMutation.mutate()}
                  disabled={compResultsMutation.isPending || !compResults.some(r => r.userId)}
                  className="px-6 py-2 rounded text-white font-bold disabled:opacity-50"
                  style={{ backgroundColor: PURPLE }}
                >
                  {compResultsMutation.isPending ? 'Ukladám...' : 'Uložiť výsledky'}
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Season Reset */}
      <div className="border-t pt-6">
        <h3 className="text-lg font-bold mb-2 text-red-600">Reset sezóny</h3>
        <p className="text-sm text-gray-500 mb-4">
          Vymaže všetky body, streaky a históriu bodov pre všetkých používateľov. Táto akcia je nevratná.
        </p>
        {!resetConfirm ? (
          <button
            onClick={() => setResetConfirm(true)}
            className="px-6 py-2 rounded border-2 border-red-500 text-red-600 font-bold hover:bg-red-50"
          >
            Resetovať sezónu
          </button>
        ) : (
          <div className="flex items-center gap-3">
            <span className="text-sm text-red-600 font-bold">Si si istý?</span>
            <button
              onClick={() => seasonResetMutation.mutate()}
              disabled={seasonResetMutation.isPending}
              className="px-6 py-2 rounded bg-red-600 text-white font-bold disabled:opacity-50"
            >
              {seasonResetMutation.isPending ? 'Resetujem...' : 'Áno, resetovať'}
            </button>
            <button
              onClick={() => setResetConfirm(false)}
              className="px-4 py-2 rounded border border-gray-300 text-gray-600"
            >
              Nie
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default GamificationTab;
